import { useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { useHistory } from 'react-router-dom';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import useClinicUser from 'shared/hooks/useClinicUser';
import { GraphQLErrorPolicies } from 'shared/enums/GraphQLErrorPolicies';
import { GetClinicUserDocument, useUpdateCurrentClinicMutation } from 'shared/types/graphql';
import { RouteDefinitions } from 'routes';
import { useAuth } from 'shared/providers/AuthProvider';
import { useAppDispatch } from 'state/hooks';
import { updateCurrentClinicState } from 'state/currentClinic/currentClinicSlice';
import { closeChannelOverlay } from 'pages/Conversations/state/conversationsSlice';

const useUpdateCurrentClinic = (): {
  updateCurrentClinic: (clinicId: string) => Promise<void>;
  loading: boolean;
} => {
  const { isImpersonating } = useAuth();
  const { clinicUser } = useClinicUser();
  const history = useHistory();
  const toast = useToast();
  const dispatch = useAppDispatch();

  const [updateCurrentClinicMutation, { loading }] = useUpdateCurrentClinicMutation({
    errorPolicy: GraphQLErrorPolicies.All,
    refetchQueries: [{ query: GetClinicUserDocument }],
  });

  const updateCurrentClinic = useCallback(
    async (clinicId: string) => {
      const hasRoleAtNextClinic = clinicUser?.vetInfo?.roles?.some(
        (role) => role.clinic && role.clinic?.id === clinicId,
      );

      if (!hasRoleAtNextClinic) {
        history.push(RouteDefinitions.Index);

        return;
      }

      try {
        if (!clinicUser?.vetInfo?.id) {
          return;
        }

        dispatch(closeChannelOverlay());
        dispatch(updateCurrentClinicState({ clinicId }));

        if (!isImpersonating) {
          const nextClinic = clinicUser?.clinics?.find((clinic) => clinic.id === clinicId);
          await updateCurrentClinicMutation({
            variables: { vetInfoId: clinicUser.vetInfo.id, clinicId },
            optimisticResponse: {
              updateOneVetInfo: {
                __typename: 'VetInfo',
                ...clinicUser.vetInfo,
                id: clinicUser.vetInfo.id,
                currentClinic: nextClinic,
              },
            },
          });
        }
      } catch (e) {
        console.error(e);
        Sentry.captureException(e);
        toast({
          status: 'error',
          description: 'Whoops! Something went wrong while loading that clinic. Please refresh the page and try again.',
        });
      }
    },
    [clinicUser?.vetInfo, clinicUser?.clinics, history, dispatch, isImpersonating, updateCurrentClinicMutation, toast],
  );

  return { updateCurrentClinic, loading };
};

export default useUpdateCurrentClinic;
